import * as h3 from "h3-js";

// Helper function to safely use H3's gridRingUnsafe
const safeGridRing = (h3Index: string, ringSize: number): string[] => {
  try {
    return h3.gridRingUnsafe(h3Index, ringSize);
  } catch (e: unknown) {
    // Return empty array for pentagon-related errors (code 9)
    if (typeof e === "object" && e !== null && "code" in e && e.code === 9) {
      return [];
    }
    // Rethrow other errors
    throw e;
  }
};

/**
 * Identifies unselected cells that are completely surrounded by selected cells
 * @param selectedH3Indexes Array of selected H3 cell indexes
 * @param maxRingSize Maximum ring size to search for surrounding cells (default: 20)
 * @returns Array of surrounded unselected H3 cell indexes
 */
export const calculateSurroundedH3Indexes = (selectedH3Indexes: string[], maxRingSize = 15): string[] => {
  const allIndexes = new Set(selectedH3Indexes);
  const innerIndexes = new Set<string>();
  let prevInnerSize = -1;

  // Identify boundary cells of the selected area
  const getBoundary = (indexes: Set<string>): Set<string> => {
    const boundary = new Set<string>();
    indexes.forEach((index) => {
      const neighbors = safeGridRing(index, 1);
      const isOnBoundary = neighbors.some((n) => !indexes.has(n));
      if (isOnBoundary) {
        boundary.add(index);
      }
    });
    return boundary;
  };

  // Find unselected cells adjacent to boundary cells as candidates
  const getCandidates = (boundaryIndexes: Set<string>, selectedIndexes: Set<string>): Set<string> => {
    const candidates = new Set<string>();
    boundaryIndexes.forEach((boundaryIndex) => {
      const neighbors = safeGridRing(boundaryIndex, 1);
      neighbors.forEach((neighbor) => {
        if (!selectedIndexes.has(neighbor) && !candidates.has(neighbor)) {
          candidates.add(neighbor);
        }
      });
    });
    return candidates;
  };

  // Check if a candidate cell is completely surrounded
  const isEnclosed = (h3Index: string): boolean => {
    const enclosedDirections = new Array(6).fill(false);

    for (let ringSize = 1; ringSize <= maxRingSize; ringSize += 1) {
      const ring = safeGridRing(h3Index, ringSize);
      if (ring.length === 0) continue;

      // Extract representative cells for 6 directions
      const directionCells = ringSize === 1 ? ring : ring.filter((_, i) => i % ringSize === 0).slice(0, 6);

      directionCells.forEach((cell, index) => {
        if (enclosedDirections[index]) return;

        if (allIndexes.has(cell) || innerIndexes.has(cell)) {
          enclosedDirections[index] = true;
        }
      });

      const isEnclosedCell = enclosedDirections.every((flag) => flag);
      if (isEnclosedCell) {
        return true;
      }
    }

    return false;
  };

  while (prevInnerSize !== innerIndexes.size) {
    prevInnerSize = innerIndexes.size;

    const currentIndexes = new Set([...allIndexes, ...innerIndexes]);
    const boundary = getBoundary(currentIndexes);
    Array.from(getCandidates(boundary, currentIndexes))
      .filter(isEnclosed)
      .forEach((candidate) => {
        innerIndexes.add(candidate);
      });
  }

  return Array.from(innerIndexes);
};
